import type { ResourceSpec } from '@scoutaround/script-breakdown-shared';

import {
  APPLICABILITY_LABELS,
  DEPARTMENT_LABELS,
  NECESSITY_LABELS,
  PLAN_CATEGORIES,
  QUANTITY_SOURCE_LABELS,
  REQUIREMENT_TYPE_LABELS,
  RESOURCE_LABELS,
  RISK_LABELS,
  categoryLabel,
  words,
} from './labels.js';

/**
 * A REVIEWED VALUE BESIDE THE CURRENT ONE, in words.
 *
 * The diff carries `unknown` on both sides (the contract's `FieldDiff`), so each
 * value is read defensively by what it looks like, and a field this build does
 * not know still reads as something rather than as "[object Object]". An absent
 * value is "Not set", never 0 or an empty cell.
 */

const FIELD_LABELS: Readonly<Record<string, string>> = {
  name: 'Name',
  description: 'Description',
  requirementType: 'Type',
  primaryCategory: 'Category',
  departments: 'Departments',
  resourceType: 'Resource',
  resourceSpec: 'Resource details',
  necessity: 'Necessity',
  condition: 'Condition',
  scenes: 'Scenes',
  quantity: 'AI count',
  riskFlags: 'Risk flags',
  specialistReview: 'Specialist review',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? words(field);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function enumWord(field: string, value: string): string {
  if (field === 'requirementType' && value in REQUIREMENT_TYPE_LABELS) {
    return REQUIREMENT_TYPE_LABELS[value as keyof typeof REQUIREMENT_TYPE_LABELS];
  }
  if (field === 'necessity' && value in NECESSITY_LABELS) return NECESSITY_LABELS[value as keyof typeof NECESSITY_LABELS];
  if (field === 'resourceType' && value in RESOURCE_LABELS) return RESOURCE_LABELS[value as keyof typeof RESOURCE_LABELS];
  if (field === 'riskFlags' && value in RISK_LABELS) return RISK_LABELS[value as keyof typeof RISK_LABELS];
  if (field === 'primaryCategory') {
    const category = PLAN_CATEGORIES.find((c) => c === value);

    return category === undefined ? words(value) : categoryLabel(category);
  }

  return value;
}

export function formatPlanValue(field: string, value: unknown, sceneLabel: (id: string) => string): string {
  if (value === null || value === undefined) return 'Not set';
  if (typeof value === 'string') return value.trim() === '' ? 'Not set' : enumWord(field, value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';

    return value
      .map((item: unknown) => {
        if (typeof item === 'string') return enumWord(field, item);
        if (isRecord(item) && typeof item.department === 'string') {
          const label = DEPARTMENT_LABELS[item.department as keyof typeof DEPARTMENT_LABELS] ?? words(item.department);
          const task = typeof item.task === 'string' && item.task !== '' ? `: ${item.task}` : '';

          return `${label}${task}`;
        }
        if (isRecord(item) && typeof item.sceneId === 'string') {
          const need = typeof item.need === 'string' && item.need !== '' ? ` (${item.need})` : '';
          const applicability =
            item.applicability === 'POSSIBLE' ? `, ${APPLICABILITY_LABELS.POSSIBLE.toLowerCase()}` : '';

          return `${sceneLabel(item.sceneId)}${need}${applicability}`;
        }

        return formatPlanValue(field, item, sceneLabel);
      })
      .join('; ');
  }

  if (isRecord(value)) {
    // The AI count: `{ value, source }`.
    if ('source' in value && 'value' in value) {
      const source =
        typeof value.source === 'string' && value.source in QUANTITY_SOURCE_LABELS
          ? QUANTITY_SOURCE_LABELS[value.source as keyof typeof QUANTITY_SOURCE_LABELS]
          : null;
      const count = typeof value.value === 'number' ? String(value.value) : 'Not stated';

      return source === null ? count : `${count} (${source})`;
    }
    // A condition: its own sentence.
    if (typeof value.text === 'string' && 'code' in value) return value.text;
    // A resource specification: the fields that are set.
    if (typeof value.type === 'string' && value.type in RESOURCE_LABELS) {
      const set = specFields(value as ResourceSpec);

      return set.length === 0 ? 'Nothing specified' : set.map((f) => `${f.label}: ${f.value}`).join('; ');
    }

    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${words(k)}: ${formatPlanValue(k, v, sceneLabel)}`)
      .join('; ');
  }

  return 'Not set';
}

const SPEC_LABELS: Readonly<Record<string, string>> = {
  quantity: 'Quantity',
  unit: 'Unit',
  dates: 'Dates',
  territory: 'Territory',
  constraints: 'Constraints',
  requiredCapabilities: 'Must be able to',
  geography: 'Geography',
  intExt: 'Interior or exterior',
  access: 'Access',
  physicalFeatures: 'Physical features',
  restrictions: 'Restrictions',
  availabilityWindow: 'Available',
  equipmentClass: 'Class',
  technicalSpecs: 'Technical specification',
  power: 'Power',
  operatorRequired: 'Operator needed',
  assetKind: 'Kind',
  resolution: 'Resolution',
  formats: 'Formats',
  usageRights: 'Usage rights',
  reconstructionQuality: 'Reconstruction quality',
  virtualProductionCompatible: 'Works for virtual production',
  role: 'Role',
  qualificationsToConfirm: 'Qualifications to confirm',
  engagementScope: 'Engagement',
  specs: 'Specification',
};

const INT_EXT: Readonly<Record<string, string | null>> = {
  INT: 'Interior',
  EXT: 'Exterior',
  INT_EXT: 'Interior and exterior',
  UNKNOWN: null,
};

/**
 * The fields of a resource specification that are SET, in words. An unknown
 * value is null in the contract ("never guessed"), so a field left out here is
 * one nobody knows — and is not drawn as a zero or an empty line.
 */
export function specFields(spec: ResourceSpec): { key: string; label: string; value: string }[] {
  const out: { key: string; label: string; value: string }[] = [];

  for (const [key, raw] of Object.entries(spec) as [string, unknown][]) {
    if (key === 'type' || raw === null || raw === undefined) continue;

    let value: string | null = null;
    if (key === 'intExt') value = typeof raw === 'string' ? (INT_EXT[raw] ?? null) : null;
    else if (key === 'dates' || key === 'availabilityWindow') {
      const range = raw as { start: string | null; end: string | null };
      value = range.start === null && range.end === null ? null : `${range.start ?? '…'} – ${range.end ?? '…'}`;
    } else if (typeof raw === 'string') value = raw.trim() === '' ? null : raw;
    else if (typeof raw === 'number') value = key === 'quantity' && spec.unit !== null ? `${String(raw)} ${spec.unit}` : String(raw);
    else if (typeof raw === 'boolean') value = raw ? 'Yes' : 'No';
    else if (Array.isArray(raw)) {
      if (raw.length === 0) continue;
      value = raw
        .map((item: unknown) =>
          isRecord(item) && typeof item.key === 'string' ? `${item.key}: ${String(item.value)}` : String(item),
        )
        .join(', ');
    }

    // The unit is said with the quantity.
    if (key === 'unit' && spec.quantity !== null) continue;
    if (value === null) continue;
    out.push({ key, label: SPEC_LABELS[key] ?? words(key), value });
  }

  return out;
}
